import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePermission from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';
import { buildImageUrl } from '../../utils/imageUrl';
import toast from 'react-hot-toast';
import {
  Plus, Trash2, Edit, Printer, Wallet, Receipt, Scale, BookOpen, X,
  Loader2, Paperclip, RefreshCw, CheckCircle2, Clock, XCircle
} from 'lucide-react';

// ── Amount in words (Indian system: lakh / crore) ────────────────────────────
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven',
  'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
function two(n) { return n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? ' ' + ONES[n % 10] : ''}`; }
function three(n) { return n >= 100 ? `${ONES[Math.floor(n / 100)]} Hundred${n % 100 ? ' ' + two(n % 100) : ''}` : two(n); }
export function amountInWords(amount) {
  const n = Math.floor(Math.abs(Number(amount) || 0));
  const paise = Math.round((Math.abs(Number(amount) || 0) - n) * 100);
  if (n === 0 && paise === 0) return 'Zero Rupees Only';
  let out = '';
  const crore = Math.floor(n / 10000000), lakh = Math.floor((n / 100000) % 100),
    thousand = Math.floor((n / 1000) % 100), rest = n % 1000;
  if (crore) out += `${two(crore)} Crore `;
  if (lakh) out += `${two(lakh)} Lakh `;
  if (thousand) out += `${two(thousand)} Thousand `;
  if (rest) out += three(rest);
  out = `${out.trim()} Rupees`;
  if (paise) out += ` and ${two(paise)} Paise`;
  return `${out} Only`;
}

const STATUS_BADGES = {
  pending:  { cls: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock, label: 'Pending Approval' },
  approved: { cls: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle2, label: 'Approved' },
  rejected: { cls: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, label: 'Rejected' },
};
const MODES = [
  { value: 'cash', label: 'Cash' }, { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' }, { value: 'bank_transfer', label: 'Bank Transfer' },
];
const fmtINR = n => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';

export default function CashBookPage() {
  const { canWrite, canDelete } = usePermission('cash_book');
  const { user } = useAuth();
  const { settings } = useSettings() || {};
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState('advances');
  const [advances, setAdvances] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [balances, setBalances] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {kind:'advance'|'expense'|'ledger', row?}
  const [saving, setSaving] = useState(false);
  const [uploadingExp, setUploadingExp] = useState(null);
  const fileInputRef = useRef(null);
  const uploadExpRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [
        api.get('/cashbook/advances'), api.get('/cashbook/expenses'),
        api.get('/cashbook/balances'), api.get('/cashbook/ledgers' + (isAdmin ? '?all=1' : '')),
      ];
      if (isAdmin) calls.push(api.get('/workers'));
      const [a, e, b, l, w] = await Promise.all(calls);
      setAdvances(a.data); setExpenses(e.data); setBalances(b.data); setLedgers(l.data);
      if (w) setWorkers((w.data || []).filter(x => x.is_active !== 0));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Cash Book');
    } finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const saveModal = async (form) => {
    setSaving(true);
    try {
      const { kind, row } = modal;
      if (kind === 'ledger') {
        if (row) await api.put(`/cashbook/ledgers/${row.id}`, form);
        else await api.post('/cashbook/ledgers', form);
      } else if (kind === 'advance') {
        if (row) await api.put(`/cashbook/advances/${row.id}`, form);
        else {
          const res = await api.post('/cashbook/advances', form);
          toast.success(res.data.message);
        }
      } else {
        if (row) await api.put(`/cashbook/expenses/${row.id}`, form);
        else {
          const res = await api.post('/cashbook/expenses', form);
          toast.success(res.data.message);
        }
      }
      setModal(null); load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const del = async (kind, row) => {
    if (!window.confirm(`Delete this pending ${kind}?`)) return;
    try {
      await api.delete(`/cashbook/${kind}s/${row.id}`);
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const pickBill = (exp) => { uploadExpRef.current = exp; fileInputRef.current?.click(); };
  const onBillChosen = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = '';
    const exp = uploadExpRef.current;
    if (!files.length || !exp) return;
    setUploadingExp(exp.id);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('images', f));
      await api.post(`/cashbook/expenses/${exp.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Bill attached'); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setUploadingExp(null); }
  };
  const delAttachment = async (a) => {
    if (!window.confirm('Delete this bill image?')) return;
    try { await api.delete(`/cashbook/attachments/${a.id}`); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  // ── Cash Payment Voucher print (per sample) ────────────────────────────────
  const printVoucher = (adv) => {
    const companyName = settings?.company_name || 'MOZI INNOVATORS LLP';
    const companyAddress = settings?.company_address || '';
    const modeBox = m => `<span class="mode"><span class="cb">${adv.payment_mode === m ? '✔' : '&nbsp;'}</span> ${MODES.find(x => x.value === m)?.label}</span>`;
    const html = `<html><head><title>Cash Payment Voucher — ${adv.voucher_no || ''}</title><style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family: Arial, sans-serif; font-size:13px; color:#111; }
      .v { max-width:700px; margin:24px auto; border:2px solid #111; padding:0; }
      .vh { text-align:center; border-bottom:2px solid #111; padding:12px; }
      .vh .cn { font-size:20px; font-weight:800; letter-spacing:.5px; }
      .vh .ca { font-size:11px; color:#333; margin-top:2px; }
      .vh .vt { font-size:14px; font-weight:700; margin-top:6px; text-decoration:underline; }
      .row { display:flex; border-bottom:1px solid #111; }
      .cell { padding:9px 12px; flex:1; }
      .cell + .cell { border-left:1px solid #111; }
      .lbl { font-size:10px; text-transform:uppercase; color:#555; font-weight:700; }
      .val { font-size:13px; font-weight:600; margin-top:2px; min-height:17px; }
      .amtbox { font-size:17px; font-weight:800; }
      .words { font-style:italic; }
      .modes { display:flex; gap:18px; }
      .mode { display:inline-flex; align-items:center; gap:5px; font-weight:600; }
      .cb { display:inline-block; width:15px; height:15px; border:1.5px solid #111; text-align:center; line-height:14px; font-size:11px; }
      .signs { display:flex; }
      .sign { flex:1; padding:34px 12px 8px; text-align:center; font-size:11px; font-weight:700; }
      .sign + .sign { border-left:1px solid #111; }
      .sign .line { border-top:1px solid #111; padding-top:5px; }
      @media print { body { print-color-adjust:exact; } .v { margin:0; } }
    </style></head><body><div class="v">
      <div class="vh"><div class="cn">${companyName}</div><div class="ca">${companyAddress}</div>
        <div class="vt">CASH PAYMENT VOUCHER</div></div>
      <div class="row">
        <div class="cell"><div class="lbl">Voucher No.</div><div class="val">${adv.voucher_no || '—'}</div></div>
        <div class="cell"><div class="lbl">Date</div><div class="val">${fmtDate(adv.advance_date || adv.approved_at)}</div></div>
      </div>
      <div class="row"><div class="cell"><div class="lbl">Paid To</div><div class="val">${adv.worker_name || ''}</div></div></div>
      <div class="row">
        <div class="cell" style="max-width:200px"><div class="lbl">Amount</div><div class="val amtbox">${fmtINR(adv.amount)}</div></div>
        <div class="cell"><div class="lbl">Amount in Words</div><div class="val words">${amountInWords(adv.amount)}</div></div>
      </div>
      <div class="row"><div class="cell"><div class="lbl">Purpose / Particulars</div><div class="val">${adv.particulars || 'Advance for site/work expenses'}</div></div></div>
      <div class="row"><div class="cell"><div class="lbl">Payment Mode</div><div class="val modes">
        ${modeBox('cash')} ${modeBox('upi')} ${modeBox('cheque')} ${modeBox('bank_transfer')}
      </div></div></div>
      <div class="signs">
        <div class="sign"><div class="line">Prepared By</div></div>
        <div class="sign"><div class="line">Approved By${adv.approved_by_name ? `<br/><span style="font-weight:400">${adv.approved_by_name}</span>` : ''}</div></div>
        <div class="sign"><div class="line">Receiver Signature</div></div>
      </div>
    </div></body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { win.print(); }, 400);
  };

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
        tab === id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="p-4 md:p-6 space-y-4">
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onBillChosen} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-blue-600" /> Cash Book
          </h1>
          <p className="text-sm text-gray-500">Advances, expenses &amp; balances — approvals flow through the Approvals inbox</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title="Refresh">
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <TabBtn id="advances" icon={Wallet} label={`Advances (${advances.length})`} />
        <TabBtn id="expenses" icon={Receipt} label={`Expenses (${expenses.length})`} />
        <TabBtn id="balances" icon={Scale} label="Balances" />
        {isAdmin && <TabBtn id="ledgers" icon={BookOpen} label={`Ledgers (${ledgers.length})`} />}
      </div>

      {/* ── ADVANCES ── */}
      {tab === 'advances' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <div className="flex justify-between items-center p-3 border-b border-gray-100">
            <span className="text-sm font-bold text-gray-700">Advances</span>
            {canWrite && (
              <button onClick={() => setModal({ kind: 'advance' })}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold">
                <Plus className="w-3.5 h-3.5" /> {isAdmin ? 'Issue Advance' : 'Request Advance'}
              </button>
            )}
          </div>
          <table className="w-full text-sm min-w-[780px]">
            <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <th className="px-4 py-3">Voucher No.</th><th className="px-4 py-3">Worker</th>
              <th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Mode</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {advances.map(a => {
                const S = STATUS_BADGES[a.status];
                return (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-blue-700">{a.voucher_no || '—'}</td>
                    <td className="px-4 py-3 font-medium">{a.worker_name}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtDate(a.advance_date)}</td>
                    <td className="px-4 py-3 text-right font-bold">{fmtINR(a.amount)}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{(a.payment_mode || '').replace('_', ' ')}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${S.cls}`}>
                        <S.icon className="w-3 h-3" /> {S.label}
                      </span>
                      {a.status === 'rejected' && a.rejected_reason && (
                        <p className="text-xs text-red-500 mt-1 max-w-[160px] truncate" title={a.rejected_reason}>{a.rejected_reason}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        {a.status === 'approved' && (
                          <button onClick={() => printVoucher(a)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" title="Print voucher">
                            <Printer className="w-4 h-4" /></button>
                        )}
                        {a.status === 'pending' && (isAdmin || a.worker_id === user?.id) && (
                          <>
                            <button onClick={() => setModal({ kind: 'advance', row: a })} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600" title="Edit">
                              <Edit className="w-4 h-4" /></button>
                            {canDelete && (
                              <button onClick={() => del('advance', a)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Delete">
                                <Trash2 className="w-4 h-4" /></button>)}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!advances.length && <tr><td colSpan={7} className="p-8 text-center text-gray-400 text-sm">No advances yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── EXPENSES ── */}
      {tab === 'expenses' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <div className="flex justify-between items-center p-3 border-b border-gray-100">
            <span className="text-sm font-bold text-gray-700">Expenses</span>
            {canWrite && (
              <button onClick={() => setModal({ kind: 'expense' })}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold">
                <Plus className="w-3.5 h-3.5" /> Add Expense
              </button>
            )}
          </div>
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Worker</th>
              <th className="px-4 py-3">Ledger</th><th className="px-4 py-3">Particulars</th>
              <th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Bills</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {expenses.map(e => {
                const S = STATUS_BADGES[e.status];
                const canTouch = e.status === 'pending' && (isAdmin || e.worker_id === user?.id);
                return (
                  <tr key={e.id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-gray-600">{fmtDate(e.expense_date)}</td>
                    <td className="px-4 py-3 font-medium">{e.worker_name}</td>
                    <td className="px-4 py-3">{e.ledger_name}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px]">{e.particulars || '—'}</td>
                    <td className="px-4 py-3 text-right font-bold">{fmtINR(e.amount)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 items-center">
                        {(e.attachments || []).map(a => (
                          <div key={a.id} className="relative group">
                            <a href={buildImageUrl(a.image_path)} target="_blank" rel="noreferrer">
                              <img src={buildImageUrl(a.image_path)} alt="bill" className="w-9 h-9 object-cover rounded border border-gray-200" />
                            </a>
                            {canTouch && (
                              <button onClick={() => delAttachment(a)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                                <X className="w-2.5 h-2.5" /></button>)}
                          </div>
                        ))}
                        {(canTouch || (isAdmin && e.status !== 'rejected')) && canWrite && (
                          <button onClick={() => pickBill(e)} disabled={uploadingExp === e.id}
                            className="p-1.5 rounded border border-dashed border-gray-300 text-gray-400 hover:text-blue-600 hover:border-blue-300" title="Attach bill">
                            {uploadingExp === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${S.cls}`}>
                        <S.icon className="w-3 h-3" /> {S.label}
                      </span>
                      {e.status === 'rejected' && e.rejected_reason && (
                        <p className="text-xs text-red-500 mt-1 max-w-[140px] truncate" title={e.rejected_reason}>{e.rejected_reason}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        {canTouch && (
                          <>
                            <button onClick={() => setModal({ kind: 'expense', row: e })} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600" title="Edit">
                              <Edit className="w-4 h-4" /></button>
                            {canDelete && (
                              <button onClick={() => del('expense', e)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Delete">
                                <Trash2 className="w-4 h-4" /></button>)}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!expenses.length && <tr><td colSpan={8} className="p-8 text-center text-gray-400 text-sm">No expenses yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── BALANCES ── */}
      {tab === 'balances' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <th className="px-4 py-3">Worker</th>
              <th className="px-4 py-3 text-right">Approved Advances</th>
              <th className="px-4 py-3 text-right">Approved Expenses</th>
              <th className="px-4 py-3 text-right">Pending Expenses</th>
              <th className="px-4 py-3 text-right">Remaining Balance</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {balances.map(b => (
                <tr key={b.worker_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{b.worker_name}</td>
                  <td className="px-4 py-3 text-right">{fmtINR(b.total_advance)}</td>
                  <td className="px-4 py-3 text-right">{fmtINR(b.total_expenses)}</td>
                  <td className="px-4 py-3 text-right text-amber-600">{fmtINR(b.pending_expenses)}</td>
                  <td className={`px-4 py-3 text-right font-bold ${b.balance < 0 ? 'text-red-600' : 'text-green-700'}`}>
                    {fmtINR(b.balance)}
                  </td>
                </tr>
              ))}
              {!balances.length && <tr><td colSpan={5} className="p-8 text-center text-gray-400 text-sm">No cash book activity yet</td></tr>}
            </tbody>
          </table>
          <p className="p-3 text-xs text-gray-400 border-t border-gray-100">
            Remaining Balance = Approved Advances − Approved Expenses. Pending expenses are not deducted until approved.
          </p>
        </div>
      )}

      {/* ── LEDGERS (admin) ── */}
      {tab === 'ledgers' && isAdmin && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex justify-between items-center p-3 border-b border-gray-100">
            <span className="text-sm font-bold text-gray-700">Expense Ledger Master</span>
            <button onClick={() => setModal({ kind: 'ledger' })}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold">
              <Plus className="w-3.5 h-3.5" /> Add Ledger
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {ledgers.map(l => (
              <div key={l.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <span className={`font-medium ${l.is_active ? 'text-gray-800' : 'text-gray-400 line-through'}`}>{l.name}</span>
                  {!!l.is_default && <span className="ml-2 text-xs text-gray-400">(default)</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setModal({ kind: 'ledger', row: l })} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => saveLedgerToggle(l)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    l.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                    {l.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <EntryModal modal={modal} workers={workers} ledgers={ledgers.filter(l => l.is_active)}
          isAdmin={isAdmin} saving={saving} onClose={() => setModal(null)} onSave={saveModal} />
      )}
    </div>
  );

  async function saveLedgerToggle(l) {
    try { await api.put(`/cashbook/ledgers/${l.id}`, { is_active: l.is_active ? 0 : 1 }); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  }
}

function EntryModal({ modal, workers, ledgers, isAdmin, saving, onClose, onSave }) {
  const { kind, row } = modal;
  const [form, setForm] = useState(() => {
    if (kind === 'ledger') return { name: row?.name || '' };
    if (kind === 'advance') return {
      worker_id: row?.worker_id || '', amount: row?.amount || '',
      advance_date: row?.advance_date ? row.advance_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      payment_mode: row?.payment_mode || 'cash', particulars: row?.particulars || '',
    };
    return {
      worker_id: row?.worker_id || '', ledger_id: row?.ledger_id || '', amount: row?.amount || '',
      expense_date: row?.expense_date ? row.expense_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      particulars: row?.particulars || '',
    };
  });
  const set = p => setForm(f => ({ ...f, ...p }));
  const titles = {
    ledger: row ? 'Edit Ledger' : 'Add Ledger',
    advance: row ? 'Edit Advance' : (isAdmin ? 'Issue Advance' : 'Request Advance'),
    expense: row ? 'Edit Expense' : 'Add Expense',
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">{titles[kind]}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        {kind === 'ledger' && (
          <F label="Ledger Name *"><input className="minp" value={form.name} onChange={e => set({ name: e.target.value })} autoFocus /></F>
        )}

        {kind === 'advance' && (
          <>
            {isAdmin && (
              <F label="Worker *">
                <select className="minp" value={form.worker_id} onChange={e => set({ worker_id: e.target.value })}>
                  <option value="">— Select worker —</option>
                  {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </F>
            )}
            <div className="grid grid-cols-2 gap-3">
              <F label="Amount (₹) *"><input className="minp" type="number" min="1" step="0.01" value={form.amount} onChange={e => set({ amount: e.target.value })} /></F>
              <F label="Date"><input className="minp" type="date" value={form.advance_date} onChange={e => set({ advance_date: e.target.value })} /></F>
            </div>
            <F label="Payment Mode">
              <select className="minp" value={form.payment_mode} onChange={e => set({ payment_mode: e.target.value })}>
                {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </F>
            <F label="Purpose / Particulars"><input className="minp" value={form.particulars} onChange={e => set({ particulars: e.target.value })} /></F>
            {form.amount > 0 && <p className="text-xs text-gray-500 italic">{amountInWords(form.amount)}</p>}
          </>
        )}

        {kind === 'expense' && (
          <>
            {isAdmin && !row && (
              <F label="Worker (blank = self)">
                <select className="minp" value={form.worker_id} onChange={e => set({ worker_id: e.target.value })}>
                  <option value="">— Self —</option>
                  {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </F>
            )}
            <F label="Expense Ledger *">
              <select className="minp" value={form.ledger_id} onChange={e => set({ ledger_id: e.target.value })}>
                <option value="">— Select ledger —</option>
                {ledgers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Amount (₹) *"><input className="minp" type="number" min="1" step="0.01" value={form.amount} onChange={e => set({ amount: e.target.value })} /></F>
              <F label="Date"><input className="minp" type="date" value={form.expense_date} onChange={e => set({ expense_date: e.target.value })} /></F>
            </div>
            <F label="Particulars"><input className="minp" value={form.particulars} onChange={e => set({ particulars: e.target.value })} placeholder="What was this spent on?" /></F>
            <p className="text-xs text-gray-400">You can attach bill images from the expense list after saving.</p>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200">Cancel</button>
          <button onClick={() => onSave(form)} disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      <style>{`.minp { width:100%; padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; font-size:13px; } .minp:focus { outline:2px solid #bfdbfe; }`}</style>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>{children}</div>;
}
